/**
 * mapkit.ts — thin wrapper around Apple's MapKit JS so curator UI can
 * call mapkit.Search for precise place coordinates. Parity with the
 * iOS MKLocalSearch flow: same POI graph, so a "Chicopee Woods" hit
 * resolves to the same pin on web + iOS.
 *
 * Token comes from GET /api/v1/curator/mapkit-token (the worker
 * signs an ES256 JWT every 30 min, isolate-cached). MapKit JS itself
 * is loaded on first use via a dynamic <script> tag so the SPA's main
 * bundle doesn't carry ~300 KB it doesn't need until a curator opens
 * the picker.
 *
 * Docs: https://developer.apple.com/documentation/mapkitjs
 */
import { req } from "./api";

const MAPKIT_SCRIPT = "https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js";

declare global {
  interface Window {
    mapkit?: any;
  }
}

let bootPromise: Promise<void> | null = null;

interface MapKitTokenResponse {
  token: string;
  expiresInSeconds: number;
}

/**
 * Loads MapKit JS + initializes it with a worker-signed JWT. Idempotent
 * — repeat calls return the same in-flight promise so we don't load
 * the script twice or burn through tokens.
 */
export async function bootMapKit(): Promise<void> {
  if (typeof window === "undefined") {
    throw new Error("MapKit JS only runs in the browser");
  }
  if (window.mapkit && (window.mapkit as any).maps) {
    return;
  }
  if (bootPromise) return bootPromise;

  bootPromise = (async () => {
    await injectScript(MAPKIT_SCRIPT);
    if (!window.mapkit) {
      throw new Error("MapKit JS failed to load");
    }
    await new Promise<void>((resolve, reject) => {
      try {
        window.mapkit!.init({
          authorizationCallback: (done: (token: string) => void) => {
            req<MapKitTokenResponse>("GET", "/api/v1/curator/mapkit-token")
              .then((r) => done(r.token))
              .catch((err) => {
                reject(err);
              });
          },
        });
        // MapKit fires "configuration-change" when init completes. There
        // isn't a guaranteed completion promise on init(), so we resolve
        // on the next tick — by then the auth callback has fired and
        // mapkit.Search is usable.
        setTimeout(resolve, 0);
      } catch (err) {
        reject(err);
      }
    });
  })();

  try {
    await bootPromise;
  } catch (err) {
    bootPromise = null;
    throw err;
  }
}

function injectScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("MapKit script failed")));
      // If already loaded, resolve immediately.
      if (window.mapkit) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => reject(new Error("MapKit script failed")));
    document.head.appendChild(script);
  });
}

export interface MapKitHit {
  name: string;
  /** "Gainesville, GA 30506" style line for the picker secondary row. */
  subtitle: string;
  latitude: number;
  longitude: number;
  /** 2-letter state code parsed off the placemark; mirrors what iOS captures. */
  stateCode: string | null;
  /** "Gainesville" — Apple's `locality`. Backfills the trail's region field. */
  city: string | null;
}

/**
 * Run a mapkit.Search against Apple Maps. Mirrors the iOS service —
 * biases towards (city, state) when provided, returns up to 10 hits.
 * The caller is expected to have already awaited bootMapKit().
 */
export async function searchMapKit(
  query: string,
  nearCity?: string,
  nearState?: string
): Promise<MapKitHit[]> {
  if (!window.mapkit) {
    throw new Error("MapKit JS not booted — call bootMapKit() first");
  }
  const composed = [query.trim(), (nearCity ?? "").trim(), (nearState ?? "").trim()]
    .filter((p) => p.length > 0)
    .join(", ");
  if (!composed) return [];

  const search = new window.mapkit.Search({
    // Continent-sized US bias mirrors the iOS service's softPrior. The
    // composed query carries the actual precision.
    region: new window.mapkit.CoordinateRegion(
      new window.mapkit.Coordinate(39.5, -98.35),
      new window.mapkit.CoordinateSpan(40, 60)
    ),
    getsUserLocation: false,
  });

  return new Promise<MapKitHit[]>((resolve, reject) => {
    search.search(composed, (err: unknown, data: any) => {
      if (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      const places = (data?.places ?? []).slice(0, 10);
      const hits: MapKitHit[] = places.map((place: any) => {
        const coord = place.coordinate;
        const subtitle =
          [place.locality, place.administrativeAreaCode || place.administrativeArea, place.postCode]
            .filter((v: string | undefined) => v && v.length > 0)
            .join(", ") || "Apple Maps result";
        return {
          name: place.name ?? place.formattedAddress ?? "Unnamed place",
          subtitle,
          latitude: coord.latitude,
          longitude: coord.longitude,
          stateCode: place.administrativeAreaCode ?? null,
          city: place.locality ?? null,
        };
      });
      resolve(hits);
    });
  });
}

/**
 * Reverse-geocode a coordinate to Apple Maps' best-guess place.
 * Used after the curator drags / drops the manual pin so the resulting
 * hit carries city + state alongside the lat/lng. Returns null silently
 * if Apple has nothing useful at that point (open ocean, etc.).
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number
): Promise<MapKitHit | null> {
  if (!window.mapkit) {
    throw new Error("MapKit JS not booted — call bootMapKit() first");
  }
  const geocoder = new window.mapkit.Geocoder({ getsUserLocation: false });
  const coord = new window.mapkit.Coordinate(latitude, longitude);
  return new Promise<MapKitHit | null>((resolve) => {
    geocoder.reverseLookup(coord, (err: unknown, data: any) => {
      if (err || !data?.results?.length) {
        resolve(null);
        return;
      }
      const place = data.results[0];
      const subtitle =
        [place.locality, place.administrativeAreaCode || place.administrativeArea, place.postCode]
          .filter((v: string | undefined) => v && v.length > 0)
          .join(", ") || "Manual location";
      resolve({
        name: place.name ?? place.formattedAddress ?? "Dropped pin",
        subtitle,
        latitude,
        longitude,
        stateCode: place.administrativeAreaCode ?? null,
        city: place.locality ?? null,
      });
    });
  });
}
